import { useState, useEffect, useCallback } from 'react';
import qs from 'qs';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  Grid,
  Table,
  Pagination,
  Group,
  Box,
  Text,
  ActionIcon,
  Tooltip,
} from '@mantine/core';
import { IconMap, IconEye, IconRoad } from '@tabler/icons-react';

import * as actions from '../../../actions/Logradouro';
import LogradouroMap from './Map';
import LogradouroSearch from './Search';
import ListLoadingOverlay from '../../../components/ListLoadingOverlay';
import EmptyState from '../../../components/EmptyState';
import { TableSkeleton } from '../../../components/TableSkeleton';
import { useAppDispatch, useAppSelector } from '../../../store/hooks';
import { qsNumber, qsString } from '../../../utils/qsHelpers';
import { useOptionalLogradourosMap } from '../../../components/Logradouros/LogradourosMapContext';

const DEFAULTS = { filterText: '', filterExtent: '', pageSize: 8, page: 1 };

interface ListFilters {
  SEARCH?: string;
  EXTENT?: unknown;
}

export default function LogradouroList() {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const results = useAppSelector((state) => state.logradouros.results);
  const loading = useAppSelector((state) => state.container.loading);

  const [page, setPage] = useState<number>(DEFAULTS.page);
  const [pageSize] = useState<number>(DEFAULTS.pageSize);
  const [geoJson, setGeoJson] = useState<unknown[] | unknown | null>(null);
  const logradourosMap = useOptionalLogradourosMap();
  const inShell = !!logradourosMap;
  const setShellGeoJson = logradourosMap?.setGeoJson;

  // Publica geoJson no overlay do LogradourosShell (mapa persistente).
  useEffect(() => {
    if (!setShellGeoJson) return;
    setShellGeoJson(geoJson);
  }, [setShellGeoJson, geoJson]);

  const executeSearch = useCallback(() => {
    const query = qs.parse(location.search, { ignoreQueryPrefix: true });

    const pg = qsNumber(query.page, DEFAULTS.page);
    const ps = qsNumber(query.pageSize, DEFAULTS.pageSize);
    const filterText = qsString(query.q, DEFAULTS.filterText);
    const filterExtent = qsString(query.e, DEFAULTS.filterExtent);

    const filters: ListFilters = {};
    if (filterText.length > 0) filters.SEARCH = filterText;
    else if (filterExtent) filters.EXTENT = JSON.parse(`[${filterExtent}]`);

    setPage(pg);
    dispatch((actions as any).list({ page: pg, pageSize: ps, filterText, filterExtent, filters }));
  }, [location.search, dispatch]);

  useEffect(() => {
    const query = qs.parse(location.search, { ignoreQueryPrefix: true });
    if (query.q || query.e) {
      executeSearch();
    } else {
      dispatch((actions as any).clearResults());
    }
  }, [location.key]);

  useEffect(() => {
    if (results?.items) {
      setGeoJson(results.items.map((x) => x.geoJson));
    } else {
      setGeoJson([]);
    }
  }, [results]);

  const total = !results ? 0 : results.total;
  const dataSource = !results ? [] : results.items;
  const totalPages = Math.max(1, Math.ceil(total / pageSize));

  const handlePageChange = (p: number) => {
    const query = qs.parse(location.search, { ignoreQueryPrefix: true });
    query.page = String(p);
    navigate('/logradouro?' + qs.stringify(query));
  };

  return (
    <Box>
      <LogradouroSearch page={page} pageSize={pageSize} />

      <Grid gap="md" mt="md">
        <Grid.Col span={{ base: 12, md: inShell ? 12 : results ? 6 : 12 }}>
          <ListLoadingOverlay loading={loading}>
            {loading && dataSource.length === 0 ? (
              <Table striped withTableBorder>
                <Table.Tbody><TableSkeleton columns={['84%', '72%', '54%']} /></Table.Tbody>
              </Table>
            ) : results && total <= 0 ? (
              <EmptyState
                label="Nenhum logradouro encontrado"
                submessage="Tente outro nome ou área no mapa."
                icon={IconRoad}
                mih={240}
              />
            ) : total > 0 ? (
              <>
                <Table striped highlightOnHover withTableBorder>
                  <Table.Thead>
                    <Table.Tr>
                      <Table.Th>Nome</Table.Th>
                      <Table.Th>Bairro</Table.Th>
                      <Table.Th style={{ width: 100 }}>Ações</Table.Th>
                    </Table.Tr>
                  </Table.Thead>
                  <Table.Tbody>
                    {dataSource.map((item) => (
                      <Table.Tr key={item.id}>
                        <Table.Td>
                          <Text size="sm" lineClamp={1} title={item.nome}>{item.nome}</Text>
                        </Table.Td>
                        <Table.Td>
                          <Text size="sm" lineClamp={1} title={item.bairro}>{item.bairro}</Text>
                        </Table.Td>
                        <Table.Td>
                          <Group gap={4} wrap="nowrap">
                            <Tooltip label="Localizar">
                              <ActionIcon variant="subtle" onClick={() => setGeoJson(item.geoJson)}>
                                <IconMap size={16} />
                              </ActionIcon>
                            </Tooltip>
                            <Tooltip label="Visualizar">
                              <ActionIcon
                                variant="subtle"
                                onClick={() => navigate(`/logradouro/visualizar/${item.id}`)}
                              >
                                <IconEye size={16} />
                              </ActionIcon>
                            </Tooltip>
                          </Group>
                        </Table.Td>
                      </Table.Tr>
                    ))}
                  </Table.Tbody>
                </Table>
                {totalPages > 1 && (
                  <Group justify="center" mt="md">
                    <Pagination total={totalPages} value={page} onChange={handlePageChange} />
                  </Group>
                )}
              </>
            ) : null}
          </ListLoadingOverlay>
        </Grid.Col>
        {!inShell && (
          <Grid.Col span={{ base: 12, md: results ? 6 : 12 }}>
            <LogradouroMap geoJson={geoJson} />
          </Grid.Col>
        )}
      </Grid>
    </Box>
  );
}
